import { isUnauthenticatedError } from '@anchorage/graphql/errors';
import { useQuery } from '@anchorage/graphql/react';
import { handleError, setUserContext } from '@anchorage/sentry';
import { useRouter } from 'next/router';
import type { JSX } from 'react';
import { createContext, useContext, useEffect, useMemo } from 'react';

import { isPortoOperator } from '../../constants/app';
import type {
  AppUserQuery,
  AppUserQueryVariables,
} from '../../generated/graphql';
import { AppUserDocument } from '../../generated/graphql';
import { getUserActiveKey } from '../../utils/getUserActiveKey';
import { LOGIN_PATH } from '../../utils/routes';

export type BaseAppUser = Omit<AppUserQuery, 'currentUserV2'> & {
  userKeyId: string;
  name: string;
  isTrader: boolean;
  email: string;
  roles: string[];
  isAdmin: boolean;
};

export type AppUser = BaseAppUser & {
  isPortoOrg: boolean;
  isLoading: boolean;
  error: Error | undefined;
};

// Role display names from the IAM system
const ADMIN_ROLE = 'Administrator';

/**
 * Extracts role display names from the roles connection
 */
const extractRoles = (
  roles: AppUserQuery['currentUserV2']['roles'],
): string[] => {
  return (
    roles?.edges
      ?.map((edge) => edge?.node?.displayName)
      .filter((name): name is string => !!name) ?? []
  );
};
const flatUserData = (data: AppUserQuery): BaseAppUser => {
  const { currentUserV2, organization, ...rest } = data;
  const roles = extractRoles(currentUserV2.roles);
  const activeKey = getUserActiveKey(currentUserV2) ?? '';

  return {
    ...rest,
    userKeyId: activeKey ?? '',
    isTrader: currentUserV2.isTrader,
    organization,
    name: currentUserV2.name,
    roles,
    email: currentUserV2.email ?? '',
    isAdmin: roles.includes(ADMIN_ROLE),
  };
};

export const AppUserContext = createContext<AppUser | Record<string, unknown>>(
  {},
);

export const AppUser = ({ children }: { children: JSX.Element }) => {
  const { pathname } = useRouter();

  const skipForLoginPage = pathname === LOGIN_PATH;

  const {
    data,
    loading: isLoading,
    error,
  } = useQuery<AppUserQuery, AppUserQueryVariables>(AppUserDocument, {
    skip: skipForLoginPage,
  });

  const isPortoOrg = isPortoOperator(data?.organization.operator);

  const appUserData = useMemo(() => {
    if (!data?.currentUserV2) {
      return;
    }
    const userData = flatUserData(data);

    return userData;
  }, [data]);

  // Set the context to include user and org ID so Sentry errors and RUM views
  // are more actionable
  useEffect(() => {
    if (!appUserData || !data) {
      return;
    }

    const orgID = data.organization.organizationKeyID;
    setUserContext({ id: appUserData.userKeyId, orgID, isPortoOrg });

    // Dynamically imported so @datadog/browser-rum stays in the chunk the app
    // starts RUM from instead of joining every page's bundle. `cancelled` stops
    // an import that resolves after unmount from tagging the logged-out session.
    let cancelled = false;
    const datadogRumModule = import('@anchorage/datadog-rum');
    datadogRumModule
      .then(({ setDatadogRumUserContext }) => {
        if (!cancelled) {
          setDatadogRumUserContext({
            userKeyID: appUserData.userKeyId,
            orgID,
            isPortoOrg,
          });
        }
      })
      .catch(handleError);

    // Logout navigates client-side to the login shell, which unmounts this
    // provider while the RUM session carries on.
    return () => {
      cancelled = true;
      datadogRumModule
        .then(({ clearDatadogRumUserContext }) => {
          clearDatadogRumUserContext();
        })
        .catch(handleError);
    };
  }, [appUserData, data, isPortoOrg]);

  // A 401 sets `error` while onUnauthenticated redirects to login, so it is
  // withheld from consumers — a failure state would render over the redirect.
  const visibleError =
    error && !isUnauthenticatedError(error) ? error : undefined;

  // Both fields are readable before `data` lands: `isLoading` so consumers can
  // tell a first load from a refetch, `visibleError` so a hard failure with no
  // data renders as an error rather than an empty page.
  const contextValue = useMemo(
    () =>
      data
        ? { ...appUserData, isPortoOrg, isLoading, error: visibleError }
        : { isLoading, error: visibleError },
    [data, appUserData, isPortoOrg, isLoading, visibleError],
  );

  return (
    <AppUserContext.Provider value={contextValue}>
      {children}
    </AppUserContext.Provider>
  );
};

export const useAppUser = (): AppUser => {
  const appUser = useContext(AppUserContext);

  if (!appUser) {
    throw new Error('You must wrap your components in a AppUser provider');
  }

  return appUser as AppUser;
};
